import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/navbar/Header';
import Footer from '../../components/footer/footer';

interface FaqItem {
  question: string;
  answer: React.ReactNode;
  key: string;
}

const FaqPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<'client' | 'professional'>('client');

  const toggleAccordion = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  const buildFaqItems = (category: 'client' | 'professional'): FaqItem[] => {
    return Object.keys(t(`faq.${category}`, { returnObjects: true })).map(key => ({
      question: t(`faq.${category}.${key}.question`),
      answer: t(`faq.${category}.${key}.answer`),
      key
    }));
  };

  const clientFaqItems = buildFaqItems('client');
  const professionalFaqItems = buildFaqItems('professional');

  const renderFaqSection = (items: FaqItem[], title: string) => (
    <div className="mb-12">
      <h2 className="text-2xl font-bold mb-6 text-primary">{title}</h2>
      <div className="space-y-4">
        {items.map((item, index) => (
          <div key={item.key} className="border rounded-lg overflow-hidden">
            <button
              className="w-full px-6 py-4 text-left font-medium text-gray-800 hover:bg-gray-50 transition-colors duration-200 flex justify-between items-center"
              onClick={() => toggleAccordion(index)}
            >
              <span>{item.question}</span>
              <svg
                className={`w-5 h-5 transform transition-transform duration-200 ${
                  activeIndex === index ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {activeIndex === index && (
              <div className="px-6 pb-4 pt-2 bg-gray-50 text-gray-700">
                <p>{item.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Header />
      
      <div className="py-16 px-4 md:px-8 lg:px-16 max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 mt-12">{t('faq.title')}</h1>
          <p className="text-xl text-gray-600">{t('faq.subtitle')}</p>
        </div>
        
        <div className="flex justify-center mb-8 space-x-4">
          <button 
            onClick={() => setActiveCategory('client')}
            className={`px-6 py-2 rounded-full transition-all ${activeCategory === 'client' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {t('faq.categories.client')}
          </button>
          <button 
            onClick={() => setActiveCategory('professional')}
            className={`px-6 py-2 rounded-full transition-all ${activeCategory === 'professional' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {t('faq.categories.professional')}
          </button>
        </div>
        
        {activeCategory === 'client' && renderFaqSection(clientFaqItems, t('faq.categories.client'))}
        {activeCategory === 'professional' && renderFaqSection(professionalFaqItems, t('faq.categories.professional'))}
        
      </div>
      
      <Footer />
    </div>
  );
};

export default FaqPage;